import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDbtTests } from '../../hooks/useDbtData';
import LoadingSpinner from '../common/LoadingSpinner';
import SearchInput from '../common/SearchInput';
import { dbtConfig } from '../../config/dbt';

/**
 * DbtTestsTable Component
 * Displays all tests for a project with filtering
 */
const DbtTestsTable = ({ projectId, statusFilter = null }) => {
  const { data: tests, isLoading, error } = useDbtTests(projectId);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTestType, setSelectedTestType] = useState('');

  // Filter tests
  const filteredTests = useMemo(() => {
    if (!tests) return [];

    return tests.filter((test) => {
      const matchesSearch =
        test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (test.description && test.description.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesType = !selectedTestType || test.test_type === selectedTestType;

      return matchesSearch && matchesType;
    });
  }, [tests, searchTerm, selectedTestType]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Error loading tests: {error.message}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            placeholder="Search tests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={selectedTestType}
          onChange={(e) => setSelectedTestType(e.target.value)}
          className="select select-bordered w-full md:w-auto"
        >
          <option value="">All Test Types</option>
          {dbtConfig.testTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Results count */}
      <div className="text-sm text-base-content/70">
        Showing {filteredTests.length} of {tests?.length || 0} tests
      </div>

      {/* Tests Table */}
      <div className="overflow-x-auto bg-white shadow-xl rounded-lg">
        <table className="table">
          <thead>
            <tr>
              <th>Test Name</th>
              <th>Type</th>
              <th>Description</th>
              <th>Dependencies</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTests.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-8 text-base-content/60">
                  {searchTerm || selectedTestType
                    ? 'No tests match your filters'
                    : 'No tests found'}
                </td>
              </tr>
            ) : (
              filteredTests.map((test) => (
                <tr key={test.id} className="hover">
                  <td>
                    <div className="font-medium">{test.name}</div>
                    {test.tags && test.tags.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {test.tags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="badge badge-xs badge-ghost">
                            {tag}
                          </span>
                        ))}
                        {test.tags.length > 3 && (
                          <span className="badge badge-xs badge-ghost">
                            +{test.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="badge badge-outline badge-sm">
                      {test.test_type}
                    </span>
                  </td>
                  <td>
                    <div className="max-w-md truncate text-sm">
                      {test.description || (
                        <span className="text-base-content/40 italic">No description</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="text-sm">
                      {test.depends_on?.length || 0}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <Link
                        to={`/dbt/projects/${projectId}/tests/${test.id}`}
                        className="btn btn-xs btn-primary"
                      >
                        View
                      </Link>
                      <Link
                        to={`/dbt/projects/${projectId}/tests/${test.id}/history`}
                        className="btn btn-xs btn-ghost"
                      >
                        History
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DbtTestsTable;
